import { useLocation, useNavigate } from 'react-router-dom';
import { Home, Package, Box, Mic, Bell, MoreHorizontal } from 'lucide-react';
import { useAlertStore } from '@/stores/alertStore';
import { useVoiceStore } from '@/stores/voiceStore';
import { cn } from '@/lib/utils';
import { useState } from 'react';
import { MoreMenu } from './MoreMenu';

interface NavItem {
  id: string;
  label: string;
  icon: React.ElementType;
  path: string;
  badge?: number;
  isAction?: boolean;
}

export function BottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { unreadCount } = useAlertStore();
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);

  // Navigation simplifiee - le VoiceButton flottant gere la saisie vocale
  const items: NavItem[] = [
    { id: 'home', label: 'Accueil', icon: Home, path: '/dashboard' },
    { id: 'inventory', label: 'Inventaire', icon: Package, path: '/inventory' },
    { id: 'products', label: 'Produits', icon: Box, path: '/products' },
    { id: 'voice', label: 'Vocal', icon: Mic, path: '#voice', isAction: true },
    { id: 'alerts', label: 'Alertes', icon: Bell, path: '/alerts', badge: unreadCount },
    { id: 'more', label: 'Plus', icon: MoreHorizontal, path: '#more', isAction: true },
  ];

  const handleNavClick = (item: NavItem) => {
    if (item.path === '#voice') {
      useVoiceStore.getState().requestOverlay();
    } else if (item.path === '#more') {
      setIsMoreMenuOpen(true);
    } else {
      navigate(item.path);
    }
  };

  const isActive = (item: NavItem) => {
    if (item.isAction) return false;
    if (item.path === '/dashboard') {
      return location.pathname === '/dashboard' || location.pathname === '/';
    }
    return location.pathname.startsWith(item.path);
  };

  return (
    <>
      <nav
        className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 md:hidden z-50 safe-area-bottom"
        role="navigation"
        aria-label="Navigation principale"
      >
        <ul className="flex justify-around items-center h-16">
          {items.map((item) => {
            const active = isActive(item);
            const Icon = item.icon;

            const isVoice = item.id === 'voice';

            return (
              <li key={item.id} className="flex-1">
                <button
                  onClick={() => handleNavClick(item)}
                  className={cn(
                    'flex flex-col items-center justify-center w-full h-full min-h-[56px] relative',
                    'transition-colors duration-200 touch-feedback',
                    isVoice
                      ? 'text-primary'
                      : active
                        ? 'text-primary'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  )}
                  aria-label={item.label}
                  aria-current={active ? 'page' : undefined}
                >
                  {/* Icon with badge */}
                  <span className={cn(
                    'relative',
                    isVoice && 'bg-primary text-white rounded-full p-2 -mt-4 shadow-lg'
                  )}>
                    <Icon
                      className={cn(isVoice ? 'w-5 h-5' : 'w-6 h-6')}
                      aria-hidden="true"
                    />
                    {item.badge !== undefined && item.badge > 0 && (
                      <span
                        className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-medium"
                        aria-label={`${item.badge} notifications non lues`}
                      >
                        {item.badge > 99 ? '99+' : item.badge}
                      </span>
                    )}
                  </span>

                  {/* Label */}
                  <span className={cn('text-[10px] mt-1 font-medium', isVoice && '-mt-0')}>
                    {item.label}
                  </span>

                  {/* Active indicator */}
                  {active && (
                    <span className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-1 bg-primary rounded-b-full" />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* More Menu */}
      <MoreMenu
        open={isMoreMenuOpen}
        onOpenChange={setIsMoreMenuOpen}
      />
    </>
  );
}
